import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useRef, useState } from 'react';
import { RiArrowUpLine } from 'react-icons/ri';
import { useDispatch, useStore } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import styles from './common.module.scss';

import actions from '@/actions';
import ConfirmSlider from '@/components/ui/ConfirmSlider';
import { FieldNumber } from '@/components/ui/kit/Field';
import LabelGroup from '@/components/ui/LabelGroup';
import useFlightControlCommand from '@/hooks/command/dji/useFlightControlCommand';
import { NotifierService as notifier } from '@/libs/Notifier';
import { executeDJICommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

const Panel = ({ data: robots }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const [isProcessing, setProcessing] = useState(false);
  const inputRefs = useRef({
    height: useRef(),
  }).current;
  const flightControlCommand = useFlightControlCommand();

  const handleBlur = (e) => {
    const { name, value } = e.target;

    inputRefs[name].current.value = Number(value).toFixed(1);
  };

  //#region Process Functions
  const process = async (robot) => {
    const height = Number(inputRefs.height.current.value);
    const { height: dockHeight, latitude, longitude } = store.getState().telemetry[robot.id].dockOsdStatus;

    try {
      await executeDJICommand(robot, flightControlCommand.takeoff(height, dockHeight, latitude, longitude));

      onSuccess(robot);
    } catch (error) {
      if (typeof error === 'object' && error?.data?.message) {
        console.debug(error.data.message);
      }
      onFailure(robot);
    }
  };
  //#endregion

  //#region Result Handlers
  // 성공 시 처리
  const onSuccess = (robot) => {
    notifier.success(`${robot.name} has been taken off successfully.`);
    setProcessing(false);
  };

  // 실패 시 처리
  const onFailure = (robot) => {
    notifier.error(`Failed to takeoff ${robot.name}.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) {
      dispatch(actions.command.resetActivePanels());
      return;
    }

    process(robots[0]);
  }, [isProcessing]);

  const doCommand = () => {
    // 처리 중 상태로 변경
    setProcessing(true);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('desc')}>Initiates vertical takeoff to the specified height</div>
      <div className={cx('form')}>
        <LabelGroup htmlFor="height" label="Height">
          <div className={cx('fieldWrapper')}>
            <FieldNumber id="height" ref={inputRefs.height} name="height" defaultValue={50} onBlur={handleBlur} />
            <div className={cx('unit')}>m</div>
          </div>
        </LabelGroup>
      </div>
      <ConfirmSlider
        width="100%"
        height={40}
        onConfirm={doCommand}
        isProcessing={isProcessing}
        labelStyle={{ fontSize: 12 }}
      />
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'Takeoff';
Command.displayMode = 'sidebar';
Command.singleRobotOnly = true;
Command.commanderOnly = true;
Command.Icon = RiArrowUpLine;
Command.Panel = Panel;

export default Command;
